import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router';
import { useLogin } from '../hooks/useLogin';
import { loginSchema, type LoginFormValues } from '../schemas/login-schema';
import { publicPaths } from '@/app/router/paths/publicPaths';
import appIcon from '@/assets/icons/icon.svg';
import { useAuthStore } from '@/core/stores/authStore';
import { Switch } from '@/shared/components/ahkinia/Switch/switch';
import { PasswordInput } from '@/shared/components/ui/PasswordInput';
import { Form, FormControl, FormField, FormItem, FormMessage } from '@/shared/components/ui/form';
import { Input } from '@/shared/components/ui/input';
import { UnifiedButton } from '@/shared/components/ui/unified-button';

export default function LoginPage() {
  const navigate = useNavigate();
  const { submitLogin, isLoading } = useLogin();
  const errorLogout = useAuthStore((s) => s.errorLogout);
  const setErrorLogout = useAuthStore((s) => s.setErrorLogout);

  const form = useForm<LoginFormValues>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: '',
      password: '',
    },
  });

  const handleSubmit = async (values: LoginFormValues) => {
    setErrorLogout(null);
    await submitLogin(values);
  };

  return (
    <div className="w-screen h-screen flex justify-center items-center">
      <div className="flex flex-col justify-center items-center p-0 gap-[2.5rem]">
        <div className="flex flex-col gap-6 ">
          <img src={appIcon} alt="Login Logo" className="w-[13rem] h-[6.875rem]" />
          <div className="flex flex-col items-center p-0 gap-[0.5rem]">
            <p className="text-light text-base font-medium">¡Te damos la bienvenida!</p>
            <p className="text-welcome-subtitle text-sm">Inicia sesión con:</p>
          </div>
        </div>
        <div className="flex flex-col items-flex-start gap-[1.5rem] w-[28.5rem]">
          {errorLogout && (
            <div
              className="w-full p-3 rounded-md bg-warn/10 border border-warn/30"
              data-testid="login-session-expired-banner"
            >
              <p className="text-sm text-warn text-center">{errorLogout}</p>
            </div>
          )}
          <Form {...form}>
            <form
              onSubmit={form.handleSubmit(handleSubmit)}
              className="flex flex-col gap-[1.5rem]"
              data-testid="login-form"
            >
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <Input
                        placeholder="Correo electrónico"
                        type="email"
                        data-testid="login-email-input"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="password"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <PasswordInput
                        placeholder="Contraseña"
                        data-testid="login-password-input"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <div className="flex justify-between">
                <Switch label="Recordar contraseña" className="text-welcome-subtitle" />
                <button
                  type="button"
                  className="text-xs text-text-link cursor-pointer bg-transparent border-0 p-0"
                  onClick={() => navigate(publicPaths.FORGOT_PASSWORD)}
                  data-testid="login-forgot-password-link"
                >
                  ¿Olvidaste tu contraseña?
                </button>
              </div>
              <div className="flex w-full flex-col items-center gap-[1rem]">
                <UnifiedButton
                  buttonType="prominent"
                  size="prominent-lg"
                  className="w-full"
                  type="submit"
                  disabled={isLoading}
                  data-testid="login-submit-button"
                >
                  {isLoading ? 'Iniciando sesión...' : 'Iniciar sesión'}
                </UnifiedButton>
              </div>
            </form>
          </Form>
        </div>
      </div>
    </div>
  );
}
